import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import LoadingScreen from "../components/LoadingScreen";
import ConfirmationModal from "../components/ConfirmationModal";
import PaywallModal from "../components/PaywallModal";
import { useEntitlementGate } from "../contexts/EntitlementGateContext";
import { saveCreationDraft, takeCreationDraft } from "../utils/creationDraft";
import CharacterSelectGrid from "../components/audiobook/CharacterSelectGrid";
import { showToast } from "../components/Toast";
import logger from "../utils/logger";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };
const VOICE_CREATE_STARS = 50;

const VOICE_LANGUAGES = [
  { label: "English", value: "en" },
  { label: "Spanish", value: "es" },
  { label: "French", value: "fr" },
  { label: "German", value: "de" },
  { label: "Italian", value: "it" },
  { label: "Portuguese", value: "pt" },
  { label: "Chinese", value: "zh" },
  { label: "Japanese", value: "ja" },
  { label: "Korean", value: "ko" },
  { label: "Hindi", value: "hi" },
];

const PREMIUM_SUBSCRIPTION_TYPES = [
  "STRIPE",
  "IAP",
  "EDDY",
  "PREMIUM",
  "NIKHIL",
  "PHILIP",
  "BYPASS",
  "STARTER",
  "PLUS",
  "PRO",
  "FAMILY",
  "UNLIMITED",
];

const LockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M7 11V8a5 5 0 0 1 10 0v3"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
    />
    <rect x="5" y="11" width="14" height="10" rx="2.5" stroke="currentColor" strokeWidth="2.4" />
  </svg>
);

const AIVoiceStorybookCreatePage = () => {
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const { user, refreshUserData, subscriptionStatus } = useAuth();
  const { requireEntitlement } = useEntitlementGate();
  // Wall-interrupted draft; restored when checkout returns here.
  const creationDraftRef = useRef(takeCreationDraft("create-voice-storybook"));

  useEffect(() => {
    const d = creationDraftRef.current;
    if (!d) return;
    if (d.selectedLanguage) setSelectedLanguage(d.selectedLanguage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCharacters, setSelectedCharacters] = useState([]);

  // Characters load async — re-select drafted ones when they arrive.
  // Must sit AFTER characters state is declared (avoids TDZ crash on mount).
  useEffect(() => {
    const d = creationDraftRef.current;
    if (!d?.selectedCharacterIds?.length || !characters.length) return;
    const picked = characters.filter((c) => d.selectedCharacterIds.includes(c.id));
    if (picked.length) setSelectedCharacters(picked);
    creationDraftRef.current = { ...d, selectedCharacterIds: null };
  }, [characters]); // eslint-disable-line react-hooks/exhaustive-deps

  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const isPremium = PREMIUM_SUBSCRIPTION_TYPES.includes(
    subscriptionStatus?.subscriptionType,
  );

  useEffect(() => {
    const loadCharacters = async () => {
      if (!apiClient || !user?.id) return;
      try {
        setLoading(true);
        const response = await apiClient.getUserCharacters(user.id);
        if (response?.status) {
          setCharacters(response.data || []);
        } else {
          showToast("Failed to load characters", "error");
        }
      } catch (error) {
        logger.error("Error loading characters for AI Voice Storybook:", error);
        showToast("Failed to load characters", "error");
      } finally {
        setLoading(false);
      }
    };
    loadCharacters();
  }, [apiClient, user?.id]);

  // Free users can only keep English selected.
  useEffect(() => {
    if (!isPremium && selectedLanguage !== "en") {
      setSelectedLanguage("en");
    }
  }, [isPremium, selectedLanguage]);

  const handleCharacterToggle = (character) => {
    setSelectedCharacters((prev) => {
      const exists = prev.find((c) => c.id === character.id);
      if (exists) return prev.filter((c) => c.id !== character.id);
      return [...prev, character];
    });
  };

  const isLanguageUnlocked = (code) => isPremium || code === "en";

  const handleLanguageSelect = (code) => {
    if (!isLanguageUnlocked(code)) {
      setShowPaywallModal(true);
      return;
    }
    setSelectedLanguage(code);
  };

  const handleContinueClick = () => {
    // Members-only gate first (no-op pre-cutover).
    if (!requireEntitlement({ trigger: "voice", contentType: "voice_storybook" })) {
      saveCreationDraft("create-voice-storybook", {
        selectedCharacterIds: selectedCharacters.map((c) => c.id),
        selectedLanguage,
      });
      return;
    }
    if (selectedCharacters.length === 0) {
      showToast("Please select at least one character first", "error");
      return;
    }
    const languageToSend = isLanguageUnlocked(selectedLanguage)
      ? selectedLanguage
      : "en";
    if (!languageToSend) {
      showToast("Please choose a language", "error");
      return;
    }
    setSelectedLanguage(languageToSend);
    setShowConfirmModal(true);
  };

  const proceedWithVoiceStorybookCreation = async () => {
    setShowConfirmModal(false);
    setIsCreating(true);
    try {
      const languageToSend = isLanguageUnlocked(selectedLanguage)
        ? selectedLanguage || "en"
        : "en";
      const response = await apiClient.createVoiceStorybook({
        characterIds: selectedCharacters.map((c) => c.id),
        language: languageToSend,
        isPublic: false,
      });

      if (response?.status === "success" && response?.audiobook_id) {
        await refreshUserData?.();
        navigate(`/audiobooks/${response.audiobook_id}`);
        return;
      }

      const errorMessage =
        response?.error || response?.detail || "Not enough stars to create an AI Voice Storybook.";
      showToast(errorMessage, "error");
    } catch (error) {
      logger.error("Error creating AI Voice Storybook:", error);
      showToast("Something went wrong creating your AI Voice Storybook. Please try again.", "error");
    } finally {
      setIsCreating(false);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading your characters..." type="character" />;
  }

  return (
    <div
      className="min-h-screen relative"
      style={{
        background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
        fontFamily: "'Inter', 'Fredoka', sans-serif",
        paddingBottom: 1,
      }}
    >
      {/* Soft brand blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div style={{ position: "absolute", top: -180, left: -160, width: 480, height: 480, borderRadius: "50%", background: "rgba(0,101,248,0.08)", filter: "blur(30px)" }} />
        <div style={{ position: "absolute", bottom: -200, right: -200, width: 520, height: 520, borderRadius: "50%", background: "rgba(0,202,255,0.08)", filter: "blur(30px)" }} />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-16">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <button
            type="button"
            onClick={() => navigate("/library?tab=storybooks")}
            className="inline-flex items-center gap-2 mb-3 transition-colors"
            style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, fontWeight: 600 }}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Storybooks
          </button>
          <h1
            style={{
              ...FREDOKA,
              color: "#0B1A33",
              fontWeight: 700,
              fontSize: "clamp(1.75rem, 3.6vw, 2.6rem)",
              letterSpacing: "-0.02em",
              textTransform: "uppercase",
              lineHeight: 1.05,
              margin: "0 0 4px",
            }}
          >
            AI Book <span style={{ color: "#0065F8" }}>Creation</span>
          </h1>
          <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 15, margin: 0 }}>
            Pick your characters — your story comes alive with a live AI voice conversation.
          </p>
        </div>

        {/* Card */}
        <div
          className="relative overflow-hidden"
          style={{
            minHeight: 460,
            background: "#FFFFFF",
            border: "2px solid rgba(11,26,51,0.06)",
            borderRadius: 28,
            boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.08)",
            padding: "24px 24px 28px",
          }}
        >
          <CharacterSelectGrid
            characters={characters}
            selectedCharacters={selectedCharacters}
            onToggle={handleCharacterToggle}
            maxSelected={Math.max(characters.length, 1)}
            heading="Pick your storytellers"
            subheading="Choose 1 or more characters to join your live AI adventure."
          />

          {/* Language — locked at create time; backend drives Live + pages */}
          <div className="mt-8 pt-6" style={{ borderTop: "2px solid rgba(11,26,51,0.06)" }}>
            <div
              style={{
                ...FREDOKA,
                display: "block",
                color: "#0B1A33",
                fontWeight: 700,
                fontSize: 15,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                marginBottom: 8,
              }}
            >
              Language <span style={{ color: "#0065F8" }}>*</span>
            </div>
            <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, margin: "0 0 12px" }}>
              Pixley will talk and write your story in this language.
            </p>
            <div className="flex flex-wrap gap-2.5">
              {VOICE_LANGUAGES.map((lang) => {
                const unlocked = isLanguageUnlocked(lang.value);
                const selected = selectedLanguage === lang.value && unlocked;
                return (
                  <button
                    key={lang.value}
                    type="button"
                    onClick={() => handleLanguageSelect(lang.value)}
                    aria-pressed={selected}
                    aria-label={
                      unlocked
                        ? lang.label
                        : `${lang.label} (Premium)`
                    }
                    className="inline-flex items-center gap-1.5 transition active:scale-[0.97]"
                    style={{
                      ...FREDOKA,
                      fontWeight: 700,
                      fontSize: 13,
                      color: selected ? "#FFFFFF" : "#0B1A33",
                      background: selected
                        ? "#0065F8"
                        : "#FFFFFF",
                      border: selected
                        ? "2px solid #0065F8"
                        : "2px solid rgba(11,26,51,0.10)",
                      borderRadius: 999,
                      padding: "10px 14px",
                      boxShadow: selected
                        ? "0px 3px 0px #073B8E"
                        : "0px 2px 0px rgba(11,26,51,0.05)",
                      cursor: "pointer",
                      opacity: unlocked ? 1 : 0.62,
                    }}
                  >
                    <span>{lang.label}</span>
                    {!unlocked && (
                      <span style={{ color: selected ? "#FFFFFF" : "#5A6B85", display: "inline-flex" }}>
                        <LockIcon />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end mt-6 gap-3">
          <button
            type="button"
            onClick={handleContinueClick}
            disabled={selectedCharacters.length === 0 || !selectedLanguage || isCreating}
            className="inline-flex items-center gap-2 px-6 py-3 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              ...FREDOKA,
              background: "#0065F8",
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: 15,
              border: "none",
              borderRadius: 999,
              boxShadow: "0px 4px 0px #073B8E, 0px 12px 28px rgba(0,101,248,0.20)",
              cursor: "pointer",
            }}
          >
            {isCreating ? "Creating…" : "Continue"}
            {!isCreating && (
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showConfirmModal}
        onConfirm={proceedWithVoiceStorybookCreation}
        onCancel={() => setShowConfirmModal(false)}
        title="Create an AI Voice Storybook?"
        starCost={VOICE_CREATE_STARS}
        currentStars={user?.pixley_stars ?? 0}
        actionType="ai_voice_storybook"
        confirmText="Create"
        cancelText="Cancel"
      />

      <PaywallModal
        isOpen={showPaywallModal}
        onClose={() => setShowPaywallModal(false)}
        starCost={0}
        currentStars={user?.pixley_stars ?? 0}
        contentType="premium"
        apiClient={apiClient}
        user={user}
      />
    </div>
  );
};

export default AIVoiceStorybookCreatePage;
